import { ExchangeRate, ConversionResult, RateCurrentStatus } from '../types/exchangeRate';

const API_BASE_URL = '/api/exchange-rates';

class ExchangeRateService {
  /**
   * Получить актуальный курс валюты
   */
  async getCurrentExchangeRate(currencyCode: string): Promise<ExchangeRate> {
    const response = await fetch(`${API_BASE_URL}/${currencyCode.toUpperCase()}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch current exchange rate for ${currencyCode}`);
    }
    
    return response.json();
  }

  /**
   * Получить курс валюты на конкретную дату
   */
  async getExchangeRateByDate(currencyCode: string, date: string): Promise<ExchangeRate> {
    const response = await fetch(`${API_BASE_URL}/${currencyCode.toUpperCase()}/${date}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rate for ${currencyCode} on ${date}`);
    }
    
    return response.json();
  }

  /**
   * Получить курсы валюты за период
   */
  async getExchangeRatesForPeriod(
    currencyCode: string, 
    startDate: string, 
    endDate: string
  ): Promise<ExchangeRate[]> {
    const params = new URLSearchParams({
      startDate,
      endDate
    });
    
    const response = await fetch(`${API_BASE_URL}/${currencyCode.toUpperCase()}/period?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rates for ${currencyCode} for period ${startDate} to ${endDate}`);
    }
    
    return response.json();
  }

  /**
   * Получить все курсы на конкретную дату
   */
  async getExchangeRatesByDate(date: string): Promise<ExchangeRate[]> {
    const response = await fetch(`${API_BASE_URL}/date/${date}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rates for date ${date}`);
    }
    
    return response.json();
  }

  /**
   * Получить последние курсы для всех валют
   */
  async getLatestExchangeRates(): Promise<ExchangeRate[]> {
    const response = await fetch(`${API_BASE_URL}/latest`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch latest exchange rates');
    }
    
    return response.json();
  }

  /**
   * Получить курсы по номеру таблицы NBP
   */
  async getExchangeRatesByTableNumber(tableNumber: string): Promise<ExchangeRate[]> {
    const response = await fetch(`${API_BASE_URL}/table/${encodeURIComponent(tableNumber)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rates for table ${tableNumber}`);
    }
    
    return response.json();
  }

  /**
   * Получить список доступных валют
   */
  async getAvailableCurrencies(): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/currencies`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch available currencies');
    }
    
    return response.json();
  }

  /**
   * Конвертировать сумму в PLN
   */
  async convertToPln(
    amount: number, 
    fromCurrency: string, 
    date: string
  ): Promise<ConversionResult> {
    const params = new URLSearchParams({
      amount: amount.toString(),
      fromCurrency: fromCurrency.toUpperCase(),
      date
    });
    
    const response = await fetch(`${API_BASE_URL}/convert-to-pln?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to convert ${amount} ${fromCurrency} to PLN`);
    }
    
    return response.json();
  }

  /**
   * Ручное обновление курсов валют
   */
  async updateExchangeRates(): Promise<ExchangeRate[]> {
    const response = await fetch(`${API_BASE_URL}/update`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error('Failed to update exchange rates');
    }
    
    return response.json();
  }

  /**
   * Ручное обновление курсов валют за конкретную дату
   */
  async updateExchangeRatesForDate(date: string): Promise<ExchangeRate[]> {
    const response = await fetch(`${API_BASE_URL}/update/${date}`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update exchange rates for date ${date}`);
    }
    
    return response.json();
  }

  /**
   * Проверить актуальность курса
   */
  async isRateCurrent(currencyCode: string, date: string): Promise<RateCurrentStatus> {
    const params = new URLSearchParams({ date });
    
    const response = await fetch(`${API_BASE_URL}/${currencyCode.toUpperCase()}/is-current?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to check if rate is current for ${currencyCode}`);
    }
    
    return response.json();
  }

  /**
   * Получить курс валюты с fallback на актуальный курс
   */
  async getExchangeRateWithFallback(currencyCode: string, preferredDate: string): Promise<ExchangeRate> {
    try {
      // Сначала пытаемся получить курс на предпочтительную дату
      return await this.getExchangeRateByDate(currencyCode, preferredDate);
    } catch (error) {
      // Если не получилось, берем актуальный курс
      console.warn(`No exchange rate found for ${currencyCode} on ${preferredDate}, using current rate`);
      return await this.getCurrentExchangeRate(currencyCode);
    }
  }

  /**
   * Форматировать курс для отображения
   */
  formatRate(rate: number, precision: number = 4): string {
    return rate.toFixed(precision);
  }

  /**
   * Форматировать сумму с валютой
   */
  formatAmountWithCurrency(amount: number, currencyCode: string, precision: number = 2): string {
    const formattedAmount = amount.toFixed(precision);
    return `${formattedAmount} ${currencyCode}`;
  }

  /**
   * Проверить, является ли валюта PLN
   */
  isPlnCurrency(currencyCode: string): boolean {
    return currencyCode.toUpperCase() === 'PLN';
  }
}

export const exchangeRateService = new ExchangeRateService();
